// Registro en memoria de DevTools: lo que el panel muestra y lo que un agente
// (cualquiera: navegador, terminal o MCP) lee por `window.__heskala` o por el
// puente de Vite. Sin React ni DOM obligatorio (todo inyectable) para probarlo.
//
// Convex: se alimenta de los mensajes del WebSocket del cliente (ver
// lib/devtools/convex-socket.ts). Cuenta por funcion:
//   - adds: suscripciones creadas (cada una ejecuta la consulta una vez)
//   - runs: resultados recibidos (ejecucion inicial + cada re-ejecucion)
//   - reruns: re-ejecuciones (runs menos la primera de cada suscripcion)
//   - active: suscripciones vivas ahora
//   - mutations / actions / errors
// "runs" es la mejor estimacion de llamadas facturadas por consultas.

import { redact, redactText } from "./redact";

export const DEVTOOLS_SCHEMA_VERSION = 1;
const RATE_WINDOW_MS = 60_000;
const MAX_ERRORS = 100;

export interface FunctionStats {
  name: string;
  kind: "query" | "mutation" | "action";
  adds: number;
  runs: number;
  reruns: number;
  active: number;
  errors: number;
  /** Ejecuciones en el ultimo minuto (runs + mutaciones + acciones). */
  perMinute: number;
  lastAt: number | null;
}

export interface DevtoolsError {
  at: number;
  source: "console" | "window" | "promise" | "convex";
  message: string;
  /** Funcion de Convex, si aplica. */
  fn?: string;
  count: number;
}

export interface LiveQueryState {
  key: string;
  subscribers: number;
  seen: boolean;
  loaded: boolean;
  lastFetchAt: number | null;
  nextAt: number | null;
}

export interface SessionInfo {
  route?: string;
  version?: string;
  convexUrl?: string;
  userId?: string;
  companyId?: string;
  roleId?: string;
  roleType?: string;
  modules?: string[];
  debugMode?: boolean;
}

export interface PageInfo {
  visibility?: string;
  online?: boolean;
  route?: string;
}

interface Entry {
  name: string;
  kind: FunctionStats["kind"];
  adds: number;
  runs: number;
  reruns: number;
  active: number;
  errors: number;
  times: number[];
  lastAt: number | null;
}

type Listener = () => void;

export class DevtoolsRegistry {
  private fns = new Map<string, Entry>();
  /** queryId -> funcion (por socket; se limpia al reconectar). */
  private queries = new Map<number, { name: string; runs: number }>();
  /** requestId -> funcion (mutaciones y acciones en vuelo). */
  private requests = new Map<number, string>();
  private chunks: string[] | null = null;
  private errorsList: DevtoolsError[] = [];
  private listeners = new Set<Listener>();
  private session: SessionInfo = {};
  private liveSource: (() => LiveQueryState[]) | null = null;
  private since: number;
  private dirty = false;
  /** Cambia en cada aviso a la UI (useSyncExternalStore). */
  version = 0;

  constructor(private readonly now: () => number = () => Date.now()) {
    this.since = now();
  }

  // ── Alimentacion ────────────────────────────────────────────────────────

  /** Mensaje que el cliente de Convex envia (JSON ya serializado). */
  recordClientMessage(raw: string): void {
    const msg = safeParse(raw);
    if (!msg) return;
    if (msg.type === "ModifyQuerySet" && Array.isArray(msg.modifications)) {
      for (const m of msg.modifications) {
        if (m.type === "Add" && typeof m.udfPath === "string") {
          const e = this.entry(m.udfPath, "query");
          e.adds++;
          e.active++;
          this.queries.set(m.queryId, { name: m.udfPath, runs: 0 });
        } else if (m.type === "Remove") {
          const q = this.queries.get(m.queryId);
          if (q) {
            const e = this.fns.get(q.name);
            if (e && e.active > 0) e.active--;
            this.queries.delete(m.queryId);
          }
        }
      }
      this.touch();
    } else if ((msg.type === "Mutation" || msg.type === "Action") && typeof msg.udfPath === "string") {
      const e = this.entry(msg.udfPath, msg.type === "Mutation" ? "mutation" : "action");
      e.runs++;
      this.hit(e);
      this.requests.set(msg.requestId, msg.udfPath);
      this.touch();
    }
  }

  /** Mensaje que llega del servidor de Convex (JSON crudo). */
  recordServerMessage(raw: string): void {
    let msg = safeParse(raw);
    if (!msg) return;
    if (msg.type === "TransitionChunk") {
      if (msg.partNumber === 0) this.chunks = [];
      if (!this.chunks) return;
      this.chunks.push(msg.chunk);
      if (this.chunks.length < msg.totalParts) return;
      msg = safeParse(this.chunks.join(""));
      this.chunks = null;
      if (!msg) return;
    }
    if (msg.type === "Transition" && Array.isArray(msg.modifications)) {
      for (const m of msg.modifications) {
        const q = this.queries.get(m.queryId);
        if (!q) continue;
        const e = this.entry(q.name, "query");
        if (m.type === "QueryUpdated" || m.type === "QueryFailed") {
          q.runs++;
          e.runs++;
          if (q.runs > 1) e.reruns++;
          this.hit(e);
          if (m.type === "QueryFailed") {
            e.errors++;
            this.addError({ source: "convex", fn: q.name, message: String(m.errorMessage ?? "Error en la consulta") });
          }
        }
      }
      this.touch();
    } else if (msg.type === "MutationResponse" || msg.type === "ActionResponse") {
      const name = this.requests.get(msg.requestId);
      this.requests.delete(msg.requestId);
      if (name && msg.success === false) {
        const e = this.fns.get(name);
        if (e) e.errors++;
        this.addError({ source: "convex", fn: name, message: String(msg.result ?? msg.errorMessage ?? "Error") });
        this.touch();
      }
    }
  }

  /** El socket se cerro: Convex re-suscribe todo con ids nuevos al reconectar. */
  socketClosed(): void {
    for (const e of this.fns.values()) e.active = 0;
    this.queries.clear();
    this.requests.clear();
    this.chunks = null;
    this.touch();
  }

  addError(error: Omit<DevtoolsError, "at" | "count">): void {
    const message = redactText(error.message).slice(0, 2_000);
    const same = this.errorsList.find((e) => e.message === message && e.source === error.source && e.fn === error.fn);
    if (same) {
      same.count++;
      same.at = this.now();
    } else {
      this.errorsList.unshift({ ...error, message, at: this.now(), count: 1 });
      if (this.errorsList.length > MAX_ERRORS) this.errorsList.length = MAX_ERRORS;
    }
    this.touch();
  }

  setSession(info: SessionInfo): void {
    this.session = { ...this.session, ...info };
    this.touch();
  }

  setLiveSource(source: () => LiveQueryState[]): void {
    this.liveSource = source;
  }

  reset(): void {
    for (const e of this.fns.values()) {
      e.adds = 0;
      e.runs = 0;
      e.reruns = 0;
      e.errors = 0;
      e.times = [];
      e.lastAt = null;
    }
    for (const q of this.queries.values()) q.runs = 1; // ya ejecutadas: no son "nuevas"
    this.errorsList = [];
    this.since = this.now();
    this.touch();
  }

  // ── Lectura ─────────────────────────────────────────────────────────────

  functions(): FunctionStats[] {
    const now = this.now();
    const out: FunctionStats[] = [];
    for (const e of this.fns.values()) {
      e.times = e.times.filter((t) => now - t < RATE_WINDOW_MS);
      if (e.adds + e.runs + e.active + e.errors === 0) continue;
      out.push({
        name: e.name,
        kind: e.kind,
        adds: e.adds,
        runs: e.runs,
        reruns: e.reruns,
        active: e.active,
        errors: e.errors,
        perMinute: e.times.length,
        lastAt: e.lastAt,
      });
    }
    return out.sort((a, b) => b.perMinute - a.perMinute || b.runs - a.runs);
  }

  top(n = 10): FunctionStats[] {
    return this.functions().slice(0, n);
  }

  totals() {
    const fns = this.functions();
    const sum = (k: "runs" | "reruns" | "active" | "perMinute" | "errors") => fns.reduce((s, f) => s + f[k], 0);
    const elapsedMin = Math.max(1 / 60, (this.now() - this.since) / 60_000);
    return {
      since: this.since,
      calls: sum("runs"),
      reruns: sum("reruns"),
      activeSubscriptions: sum("active"),
      perMinute: sum("perMinute"),
      /** Al ritmo del ultimo minuto, por hora. */
      projectedPerHour: sum("perMinute") * 60,
      averagePerMinute: Math.round(sum("runs") / elapsedMin),
      errors: sum("errors"),
    };
  }

  errors(): DevtoolsError[] {
    return [...this.errorsList];
  }

  live(): LiveQueryState[] {
    try {
      return this.liveSource?.() ?? [];
    } catch {
      return [];
    }
  }

  snapshot(extra: PageInfo = {}) {
    return redact({
      schemaVersion: DEVTOOLS_SCHEMA_VERSION,
      at: this.now(),
      session: this.session,
      page: extra,
      convex: { totals: this.totals(), functions: this.functions() },
      live: this.live(),
      errors: this.errors().slice(0, 30),
    });
  }

  /** Reporte en markdown plano para pegar en cualquier chat de IA. */
  report(extra: PageInfo = {}): string {
    const s = this.snapshot(extra);
    const t = s.convex.totals;
    const lines: string[] = [];
    lines.push(`# Reporte DevTools Heskala (${new Date(s.at).toISOString()})`, "");
    lines.push("## Sesion");
    for (const [k, v] of Object.entries({ ...s.session, ...s.page })) {
      if (v === undefined || v === "") continue;
      // Listas largas (modulos): solo la cantidad; el detalle esta en snapshot().
      lines.push(`- ${k}: ${Array.isArray(v) ? (v.length > 8 ? `${v.length} elementos` : v.join(", ")) : String(v)}`);
    }
    lines.push("", "## Convex");
    lines.push(
      `- Desde: ${new Date(t.since).toISOString()} · llamadas (consultas): ${t.calls} · re-ejecuciones: ${t.reruns}`,
      `- Suscripciones activas: ${t.activeSubscriptions} · ultimo minuto: ${t.perMinute} (~${t.projectedPerHour}/h) · errores: ${t.errors}`,
      "",
      "| Funcion | Tipo | Activas | Llamadas | Re-ejec. | /min | Errores |",
      "| --- | --- | --- | --- | --- | --- | --- |",
    );
    for (const f of s.convex.functions.slice(0, 25)) {
      lines.push(`| ${f.name} | ${f.kind} | ${f.active} | ${f.runs} | ${f.reruns} | ${f.perMinute} | ${f.errors} |`);
    }
    if (s.live.length) {
      lines.push("", "## Consultas en vivo", "", "| Consulta | Widgets | Se ve | Ultimo pedido |", "| --- | --- | --- | --- |");
      for (const l of s.live) {
        lines.push(`| ${l.key} | ${l.subscribers} | ${l.seen ? "si" : "no"} | ${l.lastFetchAt ? new Date(l.lastFetchAt).toISOString() : "-"} |`);
      }
    }
    lines.push("", "## Errores recientes");
    if (!s.errors.length) lines.push("- Ninguno");
    for (const e of s.errors.slice(0, 15)) {
      lines.push(`- [${e.source}${e.fn ? ` ${e.fn}` : ""}] x${e.count} ${e.message.split("\n")[0]}`);
    }
    return lines.join("\n");
  }

  // ── Suscripcion para la UI ──────────────────────────────────────────────

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private entry(name: string, kind: Entry["kind"]): Entry {
    let e = this.fns.get(name);
    if (!e) {
      e = { name, kind, adds: 0, runs: 0, reruns: 0, active: 0, errors: 0, times: [], lastAt: null };
      this.fns.set(name, e);
    }
    return e;
  }

  private hit(e: Entry) {
    const now = this.now();
    e.times.push(now);
    e.lastAt = now;
  }

  /** Avisa a la UI como mucho una vez por tic (los mensajes llegan en rafaga). */
  private touch() {
    if (this.dirty || this.listeners.size === 0) return;
    this.dirty = true;
    setTimeout(() => {
      this.dirty = false;
      this.version++;
      for (const l of this.listeners) l();
    }, 250);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function safeParse(raw: unknown): any {
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Registro unico de la pestana. */
export const devtools = new DevtoolsRegistry();
